import React, { useState } from 'react';
import { CreditCardIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, PlusIcon, ClockIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, UserIcon, CalendarIcon, SearchIcon } from 'lucide-react';
export function ExpenseDashboard({
  locale = 'en-US'
}) {
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  const [searchTerm, setSearchTerm] = useState('');
  // Sample data for dashboard
  const expenseStats = {
    totalExpenses: 24750.5,
    pendingApproval: 6499.75,
    reimbursed: 18250.75,
    rejected: 1250.5,
    myDrafts: 3,
    myPending: 2,
    myRejected: 1
  };
  // Sample data for recent expense claims
  const recentExpenses = [{
    id: 'EXP-2023-001',
    title: 'Client Meeting - ABC Corp',
    date: '2023-11-10',
    submittedDate: '2023-11-11',
    amount: 245.75,
    status: 'Pending Approval',
    category: 'Travel',
    submittedBy: 'John Doe'
  }, {
    id: 'EXP-2023-002',
    title: 'Office Supplies',
    date: '2023-11-08',
    submittedDate: '2023-11-09',
    amount: 89.99,
    status: 'Approved',
    category: 'Office',
    submittedBy: 'Jane Smith'
  }, {
    id: 'EXP-2023-003',
    title: 'Conference Registration',
    date: '2023-11-05',
    submittedDate: '2023-11-06',
    amount: 599.0,
    status: 'Reimbursed',
    category: 'Training',
    submittedBy: 'Robert Brown'
  }];
  // Sample data for expenses needing your approval
  const pendingApprovalExpenses = [{
    id: 'EXP-2023-007',
    title: 'Customer Workshop',
    date: '2023-11-15',
    submittedDate: '2023-11-16',
    amount: 1280.5,
    category: 'Events',
    submittedBy: 'Sarah Williams',
    department: 'Marketing'
  }, {
    id: 'EXP-2023-008',
    title: 'Trade Show Attendance',
    date: '2023-11-08',
    submittedDate: '2023-11-12',
    amount: 2450.75,
    category: 'Marketing',
    submittedBy: 'Robert Brown',
    department: 'Marketing'
  }];
  // Get status badge styling
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Reimbursed':
        return 'bg-blue-100 text-blue-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Draft':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-7503" />;
      case 'Reimbursed':
        return <DollarSignIcon className="h-5 w-5 text-blue-500" data-id="element-7504" />;
      case 'Pending Approval':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7505" />;
      case 'Rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-7506" />;
      case 'Draft':
      default:
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-7507" />;
    }
  };
  return <div data-id="element-7508">
      <div className="mb-8 flex justify-between items-center" data-id="element-7509">
        <div data-id="element-7510">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-7511">
            Expense Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-7512">
            Overview of expense claims, approvals, and reimbursements
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-7513">
          <div className="flex items-center" data-id="element-7514">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-7515" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2 border" data-id="element-7516">
              <option value="7days" data-id="element-7517">Last 7 days</option>
              <option value="30days" data-id="element-7518">Last 30 days</option>
              <option value="90days" data-id="element-7519">Last 90 days</option>
              <option value="ytd" data-id="element-7520">Year to date</option>
            </select>
          </div>
          <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7521">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-7522" />
            New Expense
          </button>
        </div>
      </div>

      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-7523">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7524">
          Expense Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7525">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7526">
            <div className="p-5" data-id="element-7527">
              <div className="flex items-center" data-id="element-7528">
                <div className="flex-shrink-0" data-id="element-7529">
                  <CreditCardIcon className="h-6 w-6 text-gray-400" data-id="element-7530" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7531">
                  <dl data-id="element-7532">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7533">
                      Total Expenses
                    </dt>
                    <dd data-id="element-7534">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7535">
                        {formatCurrency(expenseStats.totalExpenses)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-7536">
            <div className="p-5" data-id="element-7537">
              <div className="flex items-center" data-id="element-7538">
                <div className="flex-shrink-0" data-id="element-7539">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-7540" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7541">
                  <dl data-id="element-7542">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7543">
                      Pending Approval
                    </dt>
                    <dd data-id="element-7544">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7545">
                        {formatCurrency(expenseStats.pendingApproval)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7546">
            <div className="p-5" data-id="element-7547">
              <div className="flex items-center" data-id="element-7548">
                <div className="flex-shrink-0" data-id="element-7549">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" data-id="element-7550" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7551">
                  <dl data-id="element-7552">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7553">
                      Reimbursed
                    </dt>
                    <dd data-id="element-7554">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7555">
                        {formatCurrency(expenseStats.reimbursed)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7556">
            <div className="p-5" data-id="element-7557">
              <div className="flex items-center" data-id="element-7558">
                <div className="flex-shrink-0" data-id="element-7559">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-7560" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7561">
                  <dl data-id="element-7562">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7563">
                      Rejected
                    </dt>
                    <dd data-id="element-7564">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7565">
                        {formatCurrency(expenseStats.rejected)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Expenses Summary */}
      <div className="mb-8" data-id="element-7566">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7567">My Expenses</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-7568">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7569">
            <div className="p-5" data-id="element-7570">
              <div className="flex items-center" data-id="element-7571">
                <div className="flex-shrink-0" data-id="element-7572">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-7573" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7574">
                  <dl data-id="element-7575">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7576">
                      My Drafts
                    </dt>
                    <dd data-id="element-7577">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7578">
                        {expenseStats.myDrafts}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7579">
            <div className="p-5" data-id="element-7580">
              <div className="flex items-center" data-id="element-7581">
                <div className="flex-shrink-0" data-id="element-7582">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-7583" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7584">
                  <dl data-id="element-7585">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7586">
                      My Pending Claims
                    </dt>
                    <dd data-id="element-7587">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7588">
                        {expenseStats.myPending}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} data-id="element-7589">
            <div className="p-5" data-id="element-7590">
              <div className="flex items-center" data-id="element-7591">
                <div className="flex-shrink-0" data-id="element-7592">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-7593" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-7594">
                  <dl data-id="element-7595">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7596">
                      My Rejected Claims
                    </dt>
                    <dd data-id="element-7597">
                      <div className="text-lg font-medium text-gray-900" data-id="element-7598">
                        {expenseStats.myRejected}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Expenses and Pending Approvals */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-7599">
        {/* Recent Expenses */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7600">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7601">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7602">
              Recent Expense Claims
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-7603">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-7604">
            {recentExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7605">
                No recent expense claims.
              </li> : recentExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-claim-detail')} data-id="element-7606">
                  <div className="px-4 py-4 sm:px-6" data-id="element-7607">
                    <div className="flex items-center justify-between" data-id="element-7608">
                      <div className="flex items-center space-x-3" data-id="element-7609">
                        <div className="flex-shrink-0" data-id="element-7610">
                          {getStatusIcon(expense.status)}
                        </div>
                        <div data-id="element-7611">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7612">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-7613">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-7614">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(expense.status)}`} data-id="element-7615">
                          {expense.status}
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-7616">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7617" />
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Expenses Needing Your Approval */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7618">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7619">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7620">
              Needing Your Approval
            </h3>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-7621">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-7622">
            {pendingApprovalExpenses.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-7623">
                No expenses pending your approval.
              </li> : pendingApprovalExpenses.map(expense => <li key={expense.id} className="block hover:bg-gray-50 cursor-pointer" onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} data-id="element-7624">
                  <div className="px-4 py-4 sm:px-6" data-id="element-7625">
                    <div className="flex items-center justify-between" data-id="element-7626">
                      <div className="flex items-center space-x-3" data-id="element-7627">
                        <div className="flex-shrink-0" data-id="element-7628">
                          <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7629" />
                        </div>
                        <div data-id="element-7630">
                          <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-7631">
                            {expense.title}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-7632">
                            {expense.id} • Submitted{' '}
                            {formatDate(expense.submittedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-7633">
                        <p className="text-sm font-medium text-gray-900" data-id="element-7634">
                          {formatCurrency(expense.amount)}
                        </p>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7635" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-7636">
                      <div className="sm:flex" data-id="element-7637">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-7638">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-7639" />
                          {expense.submittedBy} • {expense.department}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>

      {/* Expense Category Distribution */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-7640">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7641">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7642">
            Expense Category Distribution
          </h3>
        </div>
        <div className="p-4" data-id="element-7643">
          <div className="h-64 flex items-center justify-center" data-id="element-7644">
            <div className="w-full max-w-3xl" data-id="element-7645">
              {/* Simple bar chart for expense categories */}
              <div className="space-y-4" data-id="element-7646">
                <div data-id="element-7647">
                  <div className="flex items-center justify-between text-sm" data-id="element-7648">
                    <span data-id="element-7649">Travel</span>
                    <span className="font-medium" data-id="element-7650">
                      {formatCurrency(9850.25)} (39.8%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7651">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7652">
                      <div style={{
                      width: '39.8%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500" data-id="element-7653"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7654">
                  <div className="flex items-center justify-between text-sm" data-id="element-7655">
                    <span data-id="element-7656">Training</span>
                    <span className="font-medium" data-id="element-7657">
                      {formatCurrency(4750.0)} (19.2%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7658">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7659">
                      <div style={{
                      width: '19.2%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500" data-id="element-7660"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7661">
                  <div className="flex items-center justify-between text-sm" data-id="element-7662">
                    <span data-id="element-7663">Meals</span>
                    <span className="font-medium" data-id="element-7664">
                      {formatCurrency(3250.5)} (13.1%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7665">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7666">
                      <div style={{
                      width: '13.1%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500" data-id="element-7667"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7668">
                  <div className="flex items-center justify-between text-sm" data-id="element-7669">
                    <span data-id="element-7670">Marketing</span>
                    <span className="font-medium" data-id="element-7671">
                      {formatCurrency(2450.75)} (9.9%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7672">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7673">
                      <div style={{
                      width: '9.9%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500" data-id="element-7674"></div>
                    </div>
                  </div>
                </div>
                <div data-id="element-7675">
                  <div className="flex items-center justify-between text-sm" data-id="element-7676">
                    <span data-id="element-7677">Software</span>
                    <span className="font-medium" data-id="element-7678">
                      {formatCurrency(2150.0)} (8.7%)
                    </span>
                  </div>
                  <div className="mt-1 relative pt-1" data-id="element-7679">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-7680">
                      <div style={{
                      width: '8.7%'
                    }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500" data-id="element-7681"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-7682">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7683">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7684">
            Quick Actions
          </h3>
        </div>
        <div className="p-4" data-id="element-7685">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7686">
            <button onClick={() => window.setCurrentView && window.setCurrentView('add-expense-claim')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7687">
              <div className="flex flex-col items-center" data-id="element-7688">
                <div className="bg-indigo-100 p-3 rounded-full mb-3" data-id="element-7689">
                  <PlusIcon className="h-6 w-6 text-indigo-600" data-id="element-7690" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7691">
                  New Expense Claim
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-approval')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7692">
              <div className="flex flex-col items-center" data-id="element-7693">
                <div className="bg-yellow-100 p-3 rounded-full mb-3" data-id="element-7694">
                  <ClipboardCheckIcon className="h-6 w-6 text-yellow-600" data-id="element-7695" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7696">
                  Review Approvals
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-claims')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7697">
              <div className="flex flex-col items-center" data-id="element-7698">
                <div className="bg-blue-100 p-3 rounded-full mb-3" data-id="element-7699">
                  <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-7700" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7701">
                  View All Expenses
                </span>
              </div>
            </button>
            <button onClick={() => window.setCurrentView && window.setCurrentView('expense-reports')} className="bg-white p-4 border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors duration-200" data-id="element-7702">
              <div className="flex flex-col items-center" data-id="element-7703">
                <div className="bg-green-100 p-3 rounded-full mb-3" data-id="element-7704">
                  <TrendingUpIcon className="h-6 w-6 text-green-600" data-id="element-7705" />
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-7706">
                  Run Reports
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>;
}